import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const { processOAuthSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('processing');

  useEffect(() => {
    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('No session_id in URL');
          setStatus('error');
          setTimeout(() => navigate('/login', { replace: true }), 1000);
          return;
        }

        // Check if this session_id was already processed (prevent double-loop)
        const processedKey = `oauth_processed_${sessionId}`;
        if (sessionStorage.getItem(processedKey)) {
          console.log('Session already processed, redirecting...');
          // Already processed - just redirect based on stored user
          const storedRole = sessionStorage.getItem('oauth_user_role');
          redirectByRole(storedRole || 'student');
          return;
        }

        // Mark as being processed
        sessionStorage.setItem(processedKey, 'processing');

        // Process the OAuth session
        const result = await processOAuthSession(sessionId);
        
        // Store the role for potential re-renders
        const role = result.user?.role || 'student';
        sessionStorage.setItem('oauth_user_role', role);
        
        // Mark as completed
        sessionStorage.setItem(processedKey, 'completed');
        
        // Clean up after successful redirect
        setTimeout(() => {
          sessionStorage.removeItem(processedKey);
          sessionStorage.removeItem('oauth_user_role');
        }, 5000);

        // Redirect based on role
        setStatus('success');
        redirectByRole(role);
        
      } catch (error) {
        console.error('Auth callback error:', error);
        setStatus('error');
        // Clear any partial processing state
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');
        if (sessionId) {
          sessionStorage.removeItem(`oauth_processed_${sessionId}`);
        }
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      }
    };

    const redirectByRole = (role) => {
      if (role === 'admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (role === 'teacher') {
        navigate('/mentor/dashboard', { replace: true });
      } else {
        navigate('/student/dashboard', { replace: true });
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Completing sign in...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-muted-foreground">Sign in successful! Redirecting...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-muted-foreground">Sign in failed. Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
}
